import { AlertTriangle, CheckCircle2, Clock, Inbox, RefreshCw, ShieldAlert } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import type { QueueTab, ReconciliationAlertSummary } from '../types';

interface AlertsSummaryBarProps {
  alerts?: ReconciliationAlertSummary;
  isLoading: boolean;
  onRefresh: () => void;
  activeTab: QueueTab;
  onSelectTab: (tab: QueueTab) => void;
  onOpenReplayModal?: () => void;
}

export function AlertsSummaryBar({
  alerts,
  isLoading,
  onRefresh,
  activeTab,
  onSelectTab,
  onOpenReplayModal,
}: AlertsSummaryBarProps) {
  const healthy = alerts?.healthy ?? true;
  const dlqCount = alerts?.dlqWebhooks ?? 0;
  const stalePendingCount = alerts?.stalePendingWebhooks ?? 0;
  const openCount = alerts?.openFindings ?? 0;
  const staleOpenCount = alerts?.staleOpenFindings ?? 0;

  return (
    <div data-testid="alerts-summary-bar" className="space-y-4">
      {/* Top Health Verdict Bar */}
      <div
        className={cn(
          'p-4 rounded-2xl border transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3',
          healthy
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
            : 'bg-rose-500/10 border-rose-500/40 text-rose-300',
        )}
      >
        <div className="flex items-center gap-3">
          {healthy ? (
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0 animate-pulse">
              <AlertTriangle className="w-5 h-5 text-rose-400" />
            </div>
          )}
          <div>
            <h3 className="text-sm font-bold tracking-tight">
              {healthy
                ? 'System Reconciliation Healthy'
                : 'System Attention Required — Active Reconciliation Exceptions'}
            </h3>
            <p className="text-xs text-on-surface-variant/90">
              {healthy
                ? 'All webhook delivery queues and transaction detection sweeps are balanced.'
                : 'One or more dead-letter webhooks, stalled workers, or aged findings require operator review.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-end sm:self-auto">
          {alerts?.checkedAt && (
            <span className="text-[11px] text-on-surface-variant font-mono">
              Checked {new Date(alerts.checkedAt).toLocaleTimeString('en-IN')}
            </span>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 rounded-xl border border-border-subtle bg-surface-container/60 hover:bg-surface-container text-on-surface hover:text-primary transition-all disabled:opacity-50"
            title="Refresh monitor status"
            aria-label="Refresh monitor status"
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin text-primary')} />
          </button>
        </div>
      </div>

      {/* 4 Metric Quick-Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {/* Card 1: DLQ Webhooks */}
        <motion.div
          whileHover={{ y: -2 }}
          onClick={() => onOpenReplayModal?.()}
          className={cn(
            'p-4 rounded-2xl border transition-all relative overflow-hidden',
            onOpenReplayModal && 'cursor-pointer',
            dlqCount > 0 ? 'bg-rose-500/10 border-rose-500/30' : 'glass-panel border-border-subtle hover:border-border-default',
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              DLQ Webhooks
            </span>
            <Inbox className={cn('w-4 h-4', dlqCount > 0 ? 'text-rose-400' : 'text-on-surface-variant')} />
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'font-display text-2xl sm:text-3xl font-bold font-mono',
                dlqCount > 0 ? 'text-rose-400' : 'text-on-surface',
              )}
            >
              {dlqCount}
            </span>
            <span className="text-xs text-on-surface-variant">events</span>
          </div>
          <p className="text-[11px] text-on-surface-variant mt-1 truncate">
            {dlqCount > 0 ? 'Click to replay' : 'Queue clean'}
          </p>
        </motion.div>

        {/* Card 2: Stale Pending Webhooks */}
        <motion.div
          whileHover={{ y: -2 }}
          className={cn(
            'p-4 rounded-2xl border transition-all glass-panel relative overflow-hidden',
            stalePendingCount > 0 ? 'border-amber-500/40 bg-amber-500/10' : 'border-border-subtle',
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              Stale Webhooks
            </span>
            <Clock className={cn('w-4 h-4', stalePendingCount > 0 ? 'text-amber-400' : 'text-on-surface-variant')} />
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'font-display text-2xl sm:text-3xl font-bold font-mono',
                stalePendingCount > 0 ? 'text-amber-400' : 'text-on-surface',
              )}
            >
              {stalePendingCount}
            </span>
            <span className="text-xs text-on-surface-variant">&gt;10 min</span>
          </div>
          <p className="text-[11px] text-on-surface-variant mt-1 truncate">
            {stalePendingCount > 0 ? 'Worker lag detected' : 'In-flight normal'}
          </p>
        </motion.div>

        {/* Card 3: Open Findings */}
        <motion.div
          whileHover={{ y: -2 }}
          onClick={() => onSelectTab('OPEN')}
          className={cn(
            'p-4 rounded-2xl border transition-all cursor-pointer relative overflow-hidden',
            activeTab === 'OPEN'
              ? 'border-primary ring-1 ring-primary'
              : 'border-border-subtle hover:border-border-default',
            openCount > 0 ? 'bg-amber-500/10' : 'glass-panel',
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              Open Findings
            </span>
            <AlertTriangle className={cn('w-4 h-4', openCount > 0 ? 'text-amber-400' : 'text-on-surface-variant')} />
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'font-display text-2xl sm:text-3xl font-bold font-mono',
                openCount > 0 ? 'text-amber-400' : 'text-on-surface',
              )}
            >
              {openCount}
            </span>
            <span className="text-xs text-on-surface-variant">discrepancies</span>
          </div>
          <p className="text-[11px] text-on-surface-variant mt-1 truncate">
            {openCount > 0 ? 'Active work queue' : 'Zero discrepancies'}
          </p>
        </motion.div>

        {/* Card 4: Stale Open Findings (>4h) */}
        <motion.div
          whileHover={{ y: -2 }}
          onClick={() => onSelectTab('STALE')}
          className={cn(
            'p-4 rounded-2xl border transition-all cursor-pointer relative overflow-hidden',
            activeTab === 'STALE'
              ? 'border-primary ring-1 ring-primary'
              : 'border-border-subtle hover:border-border-default',
            staleOpenCount > 0 ? 'bg-rose-500/10' : 'glass-panel',
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              Stale Findings
            </span>
            <ShieldAlert className={cn('w-4 h-4', staleOpenCount > 0 ? 'text-rose-400' : 'text-on-surface-variant')} />
          </div>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'font-display text-2xl sm:text-3xl font-bold font-mono',
                staleOpenCount > 0 ? 'text-rose-400' : 'text-on-surface',
              )}
            >
              {staleOpenCount}
            </span>
            <span className="text-xs text-on-surface-variant">&gt;4 hours</span>
          </div>
          <p className="text-[11px] text-on-surface-variant mt-1 truncate">
            {staleOpenCount > 0 ? 'Sentry alert active' : 'No stale items'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
